// The classroom study (E1-E4), as a condition session's page sees it.
//
// A study experiment's condition rows carry test_code.study, written by the backend at
// publish (services/experimentService.js): { exerciseId, probeSet, aiDelaySeconds, tutor }.
// That is all a student's page needs - which tutor variant a condition runs stays on the
// server. tutor only says which chat the page shows: 'cc' for the Code Coach substudy's
// conditions (study/cc/CcChat.jsx), never which of its two arms; the main study's rows have
// none. A session without the block is not part of the study: every helper here then returns
// null or false, and regular sessions behave exactly as before.

// session.test_code -> an object, or null (older rows store it as a JSON string).
export const parseTestCode = (testCode) => {
  if (!testCode) return null
  if (typeof testCode === 'object') return testCode
  try {
    const parsed = JSON.parse(testCode)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (error) {
    return null
  }
}

// { exerciseId, probeSet, aiDelaySeconds, tutor } for a study condition, or null. tutor: 'cc'
// or null (any other value is read as the main study's chat).
export const getStudyConfig = (session) => {
  const study = parseTestCode(session?.test_code)?.study
  if (!study || typeof study !== 'object') return null
  const delay = Number(study.aiDelaySeconds)
  return {
    exerciseId: typeof study.exerciseId === 'string' && study.exerciseId ? study.exerciseId : null,
    probeSet: typeof study.probeSet === 'string' && study.probeSet ? study.probeSet : null,
    aiDelaySeconds: Number.isFinite(delay) && delay > 0 ? delay : 0,
    tutor: study.tutor === 'cc' ? 'cc' : null,
  }
}

export const isStudySession = (session) => getStudyConfig(session) !== null

// Whether the condition's chat is the Code Coach substudy's (CC-O or CC-M).
export const isCcStudy = (session) => getStudyConfig(session)?.tutor === 'cc'

// What a study condition's page logs about its task when it opens (SESSION_START): the exercise
// id, and the versions its exercise carries in test_code.versions - the task text, the provided
// code and the tests (E3 on; null where an exercise has none). Null outside the study.
export const studyTaskMetadata = (session) => {
  const study = getStudyConfig(session)
  if (!study) return null
  const versions = parseTestCode(session?.test_code)?.versions
  const version = (value) => (typeof value === 'string' && value ? value : null)
  return {
    exercise_id: study.exerciseId,
    task_version: version(versions?.task),
    provided_code_version: version(versions?.providedCode),
    test_version: version(versions?.tests),
  }
}

// The coding phase, on the server's clock: the experiment's Start writes started_at with the
// server's time to every condition at once, and duration_minutes is its length. Null before
// Start, or for a session without a timer.
export const codingWindow = (session) => {
  if (!session?.started_at || session?.duration_minutes == null) return null
  const startedAtMs = new Date(session.started_at).getTime()
  const minutes = Number(session.duration_minutes)
  if (!Number.isFinite(startedAtMs) || !Number.isFinite(minutes)) return null
  return { startedAtMs, endsAtMs: startedAtMs + minutes * 60 * 1000 }
}

// When a delayed condition's AI chat opens (ms since 1970), or null when it never waits: no
// delay, not a study session, or not started yet.
export const aiUnlockAtMs = (session, study = getStudyConfig(session)) => {
  if (!study || !(study.aiDelaySeconds > 0)) return null
  const window = codingWindow(session)
  return window ? window.startedAtMs + study.aiDelaySeconds * 1000 : null
}

// Whether the coding time is over at `nowMs` (use useServerNow's value, so a student's
// wrong computer clock does not move it). A study condition with probes shows them from
// this moment. Do not pause a study experiment: during a pause started_at stays as it was,
// so a long pause would run past the end time too.
export const codingTimeIsUp = (session, nowMs) => {
  const window = codingWindow(session)
  return Boolean(window) && nowMs >= window.endsAtMs
}
